﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_BUSINESS_SUCCESS, FINAL_BUSINESS_REQUEST, INITIAL_BUSINESS_REQUEST } from "@/redux/types/maintainers/business.type";

import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission, toast } from "@/services/Utils";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import BusinessTableHead from "./BusinessTableHead";
import BusinessTableBody from "./BusinessTableBody";
import BusinessForm from "./BusinessForm/BusinessForm";

const Business = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers } = useSelector((state) => state.maintainers_business);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [permissions, setPermissions] = useState([]);

	const [showDelete, setShowDelete] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_BUSINESS_REQUEST });
		api.get("/admin/business", {
			page,
			paginate,
			order,
			orderType,
			search,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, order, orderType, paginate, page]);

	const updateData = (response) => {
		const { business, customers, permissions: allPermission } = response;
		dispatch({
			type: FETCH_BUSINESS_SUCCESS,
			payload: {
				business: business.data,
				last_page: business.last_page,
				from: business.from,
				to: business.to,
				quantity: business.total,
				customers: customers,
			},
		});
		setPermissions(allPermission);
		setLoaded(true);
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_BUSINESS_REQUEST });
		api.post("/admin/business/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			if (Object.prototype.hasOwnProperty.call(response, "business")) {
				toast(addToast, "Razón social eliminada correctamente", "success");
				updateData(response);
			} else {
				dispatch({ type: FINAL_BUSINESS_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/business/save", {
			...form,
			page,
			paginate,
			order,
			orderType,
			search,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
			modalFetching(false);
			afterSave();
		});
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Razones sociales"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nueva razón social"}
				action={() => navigate("/configuracion/razones_sociales/crear")}
				back_url={"/configuracion?section=general"}
			/>

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					<Col md={12} className={classes.main_col}>
						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<BusinessTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<BusinessTableBody handleDelete={handleDelete} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
					<BusinessForm customers={customers} confirmSave={confirmSave} permissions={permissions} />
				</>
			)}
		</>
	);
};

export default Business;
